import type { FC } from 'react'

import { Table } from '@skytech/ui'
import { Td } from '@skytech/ui/Table/Td'
import { COUNTRY_FLAGS, countryOptions, getQueryFilter, getSortedList } from '@skytech/utils'

import type { CostObject } from '@/api/demoCustomersApi'
import { useAppContext } from '@/App.context'
import { Translation } from '@/components/i18n/Translation'
import { ResultCount } from '@/components/ResultCount'
import Highlight from '@/components/util/Highlight'
import NoResults from '@/components/util/NoResults'
import useRenderLimit from '@/hooks/render-limit-hook'
import { useSortParams } from '@/hooks/searchParams'

import { ClipboardButton } from './ClipboardButton'

export const EmployeesTable: FC<{
  searchFilter: string
  employees: CostObject[]
}> = ({ searchFilter, employees }) => {
  const { locale } = useAppContext()

  const [{ querySort, querySortBy }] = useSortParams()
  const stringMatcherForEmployees = (item: CostObject) => [
    item.name || '',
    item.departmentName || '',
    item.departmentCode || '',
    item.countryId || '',
    item.phoneNumber || '',
    item.accessCode || '',
  ]
  const employeesFiltered =
    employees?.filter(getQueryFilter(searchFilter, stringMatcherForEmployees)) || []
  const sortedList = getSortedList({ list: employeesFiltered, querySort, querySortBy, locale })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList || [])

  return (
    <>
      <ResultCount count={visibleList.length} />
      <Table rowHeight="tall">
        <thead>
          <tr>
            <th className="p-4 normal-case">
              <Translation id="Name" />
            </th>
            <th className="p-4 normal-case">
              <Translation id="Department" />
            </th>
            <th className="center hidden p-4 normal-case sm:table-cell">
              <Translation id="Department code" />
            </th>
            <th className="center hidden p-4 normal-case sm:table-cell">
              <Translation id="Country" />
            </th>
            <th className="center hidden p-4 normal-case sm:table-cell">
              <Translation id="Phone number" />
            </th>
            <th className="center hidden p-4 normal-case sm:table-cell">
              <Translation id="Access code" />
            </th>
          </tr>
        </thead>
        <tbody>
          {visibleList.map(item => (
            <EmployeeListItem key={item.id} searchFilter={searchFilter} {...item} />
          ))}
        </tbody>
      </Table>
      {visibleList.length === 0 && (
        <div className="my-4">
          <NoResults />
        </div>
      )}
      <ListLoaderComponent />
    </>
  )
}

const EmployeeListItem: FC<{
  searchFilter: string
  name: string
  phoneNumber: string
  countryId: number
  customerId: number
  departmentCode: string
  departmentName: string
  accessCode: string
}> = ({
  searchFilter,
  name,
  phoneNumber,
  countryId,
  departmentCode,
  departmentName,
  accessCode,
}) => {
  const countryName = countryOptions.find(country => country.value === countryId)?.label
  const countryNameWithFlag = countryName && `${COUNTRY_FLAGS[countryName]}  ${countryName}`

  return (
    <tr>
      <Td>
        <Highlight pattern={searchFilter} text={name} />
      </Td>
      <Td>
        <Highlight pattern={searchFilter} text={departmentName} />
      </Td>
      <Td className="center hidden sm:table-cell">
        <Highlight pattern={searchFilter} text={departmentCode} />
      </Td>
      <Td className="center hidden sm:table-cell">
        <div>
          <Highlight pattern={searchFilter} text={`Code: ${countryId.toString()}`} />
        </div>
        <div className="text-sm text-[#888]">
          <Highlight pattern={searchFilter} text={countryNameWithFlag} />
        </div>
      </Td>

      <Td className="center hidden sm:table-cell">
        <Highlight pattern={searchFilter} text={phoneNumber} />
        <ClipboardButton text={phoneNumber.replace('0', '')} />
      </Td>
      <Td className="center hidden sm:table-cell">
        <Highlight pattern={searchFilter} text={accessCode} />
        <ClipboardButton text={accessCode} />
      </Td>
    </tr>
  )
}
